import { useState, useEffect } from 'react';
import { Save, Tag, UserCheck, Shuffle, Settings, Plus, Trash2 } from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import { teamMembersService, type TeamMember } from '@/services/team-members.service';
import { teamsService } from '@/services/teams.service';
import { ticketTypesService } from '@/services/ticket-types.service';
import type { TicketType } from '@centraoneplus/shared-types';
import type { TeamWithMembers } from '@/types/team';
import {
  orderTeamsForPicker,
  teamOptionLabel,
} from '@/components/settings/TeamsSettings/teamHierarchy';
import type { Pipeline, PipelineRoutingConfig } from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

type TicketTypeRule = NonNullable<PipelineRoutingConfig['ticketTypeRules']>[number];

interface RoutingTabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
}

const defaultRoutingConfig: PipelineRoutingConfig = {
  autoAssignStrategy: 'none',
};

const STRATEGIES: { value: PipelineRoutingConfig['autoAssignStrategy']; label: string; description: string }[] = [
  { value: 'none', label: 'Manual', description: 'Tickets are not auto-assigned' },
  { value: 'round_robin', label: 'Round Robin', description: 'Distribute evenly across agents' },
  { value: 'least_busy', label: 'Least Busy', description: 'Assign to agent with fewest open tickets' },
  { value: 'random', label: 'Random', description: 'Randomly assign to available agents' },
];

const CHANNELS = ['email', 'chat', 'phone', 'web', 'whatsapp', 'social'];

export default function RoutingTab({ pipeline, onUpdate }: RoutingTabProps) {
  const [config, setConfig] = useState<PipelineRoutingConfig>(
    pipeline.routingConfig || defaultRoutingConfig
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [agents, setAgents] = useState<TeamMember[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [teams, setTeams] = useState<TeamWithMembers[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [isLoadingTicketTypes, setIsLoadingTicketTypes] = useState(true);

  // Fetch agents on mount
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setIsLoadingAgents(true);
        const response = await teamMembersService.getTeamMembers({ status: 'active' });
        setAgents(response.data);
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setIsLoadingAgents(false);
      }
    };
    fetchAgents();
  }, []);

  // Fetch teams scoped to this pipeline (or unscoped) on mount
  useEffect(() => {
    let cancelled = false;
    const fetchTeams = async () => {
      try {
        setIsLoadingTeams(true);
        setTeamsError(null);
        // Show all teams; backend can filter by pipelineId if you prefer narrowing.
        const res = await teamsService.getTeams({ isActive: true, limit: 200 });
        // Parents first, each followed by its sub-teams.
        if (!cancelled) setTeams(orderTeamsForPicker(res.data));
      } catch (err) {
        if (!cancelled) setTeamsError('Failed to load teams');
        console.error('Error fetching teams:', err);
      } finally {
        if (!cancelled) setIsLoadingTeams(false);
      }
    };
    fetchTeams();
    return () => {
      cancelled = true;
    };
  }, [pipeline.id]);

  // Fetch active ticket types for the "Route by Ticket Type" editor on mount.
  // Non-fatal: an empty list just disables the type selects.
  useEffect(() => {
    let cancelled = false;
    const fetchTicketTypes = async () => {
      try {
        setIsLoadingTicketTypes(true);
        const types = await ticketTypesService.getTicketTypes({ active: true });
        if (!cancelled) setTicketTypes(types);
      } catch (err) {
        console.error('Error fetching ticket types:', err);
      } finally {
        if (!cancelled) setIsLoadingTicketTypes(false);
      }
    };
    fetchTicketTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field: string, value: unknown) => {
    setConfig({ ...config, [field]: value });
    setHasChanges(true);
  };

  // ── Route by Ticket Type rule helpers ──
  const ticketTypeRules: TicketTypeRule[] = config.ticketTypeRules ?? [];

  const addTicketTypeRule = () => {
    handleChange('ticketTypeRules', [...ticketTypeRules, { ticketTypeId: '' }]);
  };

  const updateTicketTypeRule = (index: number, patch: Partial<TicketTypeRule>) => {
    const next = ticketTypeRules.map((rule, i) =>
      i === index ? { ...rule, ...patch } : rule,
    );
    handleChange('ticketTypeRules', next);
  };

  const removeTicketTypeRule = (index: number) => {
    handleChange(
      'ticketTypeRules',
      ticketTypeRules.filter((_, i) => i !== index),
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await pipelineService.updateRoutingConfig(pipeline.id, config);
      onUpdate({ ...pipeline, routingConfig: config });
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving routing config:', err);
      setError('Failed to save routing configuration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Default Assignment */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <UserCheck className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Default Assignment</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Set default team or agent for new tickets
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Default Team
            </label>
            <ThemedSelect
              value={config.defaultTeamId || ''}
              onChange={(e) => handleChange('defaultTeamId', e.target.value || undefined)}
              disabled={isLoadingTeams}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            >
              <option value="">No default team</option>
              {isLoadingTeams ? (
                <option value="" disabled>Loading teams...</option>
              ) : (
                teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {teamOptionLabel(team)}
                  </option>
                ))
              )}
            </ThemedSelect>
            {teamsError ? (
              <p className="text-xs text-red-500 mt-1">{teamsError}</p>
            ) : !isLoadingTeams && teams.length === 0 ? (
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                No teams yet — create one in <strong>Settings → Teams</strong>.
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Default Agent
            </label>
            <ThemedSelect
              value={config.defaultAgentId || ''}
              onChange={(e) => handleChange('defaultAgentId', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              disabled={isLoadingAgents}
            >
              <option value="">No default agent</option>
              {isLoadingAgents ? (
                <option value="" disabled>Loading agents...</option>
              ) : (
                agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.displayName || agent.firstName || agent.email}
                  </option>
                ))
              )}
            </ThemedSelect>
          </div>
        </div>

        {!config.defaultTeamId && !config.defaultAgentId && (
          <p className="text-xs text-red-600 mt-4 dark:text-red-400">
            <b>Note:</b> No default team or agent is selected — <strong>all agents in the organization</strong> are
            considered and assigned using the strategy selected below — but only when no team
            routing (channel / ticket type) matches.
          </p>
        )}
      </Card>

      {/* Auto-Assignment Strategy */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shuffle className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Auto-Assignment Strategy</h3>
        </div>

        <div className="space-y-3">
          {STRATEGIES.map((strategy) => (
            <label
              key={strategy.value}
              className={`
                flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors
                ${config.autoAssignStrategy === strategy.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50'
                }
              `}
            >
              <input
                type="radio"
                name="strategy"
                value={strategy.value}
                checked={config.autoAssignStrategy === strategy.value}
                onChange={(e) => handleChange('autoAssignStrategy', e.target.value)}
                className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{strategy.label}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{strategy.description}</p>
              </div>
            </label>
          ))}
        </div>
      </Card>

      {/* Channel Routing */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Settings className="h-5 w-5 text-purple-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Channel Routing</h3>
        </div>

        <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
          Route tickets to specific teams based on the channel they came from
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Channel</th>
                <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Route To Team</th>
              </tr>
            </thead>
            <tbody>
              {CHANNELS.map((channel) => {
                const rule = config.channelRules?.find((r) => r.channel === channel);

                return (
                  <tr key={channel} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 px-3">
                      <span className="capitalize">{channel}</span>
                    </td>
                    <td className="py-2 px-3">
                      <ThemedSelect
                        value={rule?.teamId || ''}
                        onChange={(e) => {
                          const newRules = [...(config.channelRules || [])];
                          const existingIndex = newRules.findIndex((r) => r.channel === channel);

                          if (e.target.value) {
                            if (existingIndex >= 0) {
                              newRules[existingIndex] = { ...newRules[existingIndex], teamId: e.target.value };
                            } else {
                              newRules.push({ channel, teamId: e.target.value });
                            }
                          } else if (existingIndex >= 0) {
                            newRules.splice(existingIndex, 1);
                          }

                          handleChange('channelRules', newRules);
                        }}
                        disabled={isLoadingTeams}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                      >
                        <option value="">Use default</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {teamOptionLabel(team)}
                          </option>
                        ))}
                      </ThemedSelect>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Route by Ticket Type */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Tag className="h-5 w-5 text-orange-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Route by Ticket Type</h3>
        </div>

        <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
          Route tickets to a specific team (and optionally a specific agent) based on their Ticket Type.
          Ticket Type rules take precedence over the default assignment and are evaluated after channel rules.
        </p>

        {ticketTypeRules.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Ticket Type</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Route To Team</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Agent (optional)</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {ticketTypeRules.map((rule, index) => (
                  <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 px-3">
                      <ThemedSelect
                        value={rule.ticketTypeId || ''}
                        onChange={(e) => updateTicketTypeRule(index, { ticketTypeId: e.target.value })}
                        disabled={isLoadingTicketTypes}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                      >
                        <option value="">Select ticket type…</option>
                        {ticketTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </ThemedSelect>
                    </td>
                    <td className="py-2 px-3">
                      <ThemedSelect
                        value={rule.teamId || ''}
                        onChange={(e) =>
                          updateTicketTypeRule(index, { teamId: e.target.value || undefined })
                        }
                        disabled={isLoadingTeams}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                      >
                        <option value="">Use default</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {teamOptionLabel(team)}
                          </option>
                        ))}
                      </ThemedSelect>
                    </td>
                    <td className="py-2 px-3">
                      <ThemedSelect
                        value={rule.agentId || ''}
                        onChange={(e) =>
                          updateTicketTypeRule(index, { agentId: e.target.value || undefined })
                        }
                        disabled={isLoadingAgents}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 disabled:opacity-50 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                      >
                        <option value="">No specific agent</option>
                        {agents.map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.displayName || agent.firstName || agent.email}
                          </option>
                        ))}
                      </ThemedSelect>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        type="button"
                        onClick={() => removeTicketTypeRule(index)}
                        aria-label="Remove ticket type rule"
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {ticketTypeRules.length === 0 && (
          <p className="text-sm text-gray-400 mb-4 dark:text-gray-500">
            No ticket type rules yet. Add one to route a Ticket Type to a specific team or agent.
          </p>
        )}

        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={addTicketTypeRule}>
            <Plus className="h-4 w-4 mr-2" />
            Add Ticket Type Rule
          </Button>
        </div>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Routing Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
